import { Box, Tabs, TabList, TabPanels, Tab, TabPanel, Button, VStack, Text, Icon, Center, HStack, Switch, FormControl, FormLabel, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { FaEye, FaTimes } from 'react-icons/fa';
import { useState, useRef, useEffect } from 'react';
import { useAudioContext } from '../context/AudioContext';
import { useDisplayNames } from '../context/DisplayNamesContext';
import PickYourFaveView from './PickYourFaveView';
import GuessTheTrackView from './GuessTheTrackView';
import TooltipKeyBox from './TooltipKeyBox';

interface BlindTestViewProps {
  playerNumberToFileId?: Map<number, string>;
}

const BlindTestView: React.FC<BlindTestViewProps> = ({ playerNumberToFileId }) => {
  const { audioElements, selectedPlayer, setBlindTestMode, pauseAll, seekToTime, isPlaying, setLastStartPositions, blindTestAllowRepeats, setBlindTestAllowRepeats, blindTestMode } = useAudioContext();
  const { getCustomDisplayName } = useDisplayNames();
  const [currentTrackName, setCurrentTrackName] = useState<string>('');
  const [displayedTrackName, setDisplayedTrackName] = useState<string>('');
  const [isRevealed, setIsRevealed] = useState(false);
  const [showPosition, setShowPosition] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const positionMarkerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Update currentTrackName when selectedPlayer changes
  useEffect(() => {
    const audioElement = audioElements.get(selectedPlayer);
    if (audioElement) {
      console.log('Audio element found:', audioElement);
      // Use custom display name if available, otherwise use original title
      const fileId = playerNumberToFileId?.get(selectedPlayer);
      const customName = fileId ? getCustomDisplayName(fileId) : '';
      const displayName = customName || audioElement.title;
      setCurrentTrackName(displayName);
      setDuration(audioElement.duration);
      // Hide the track name whenever the player changes
      setIsRevealed(false);
    } else {
      console.log('No audio element found for player:', selectedPlayer);
    }
  }, [selectedPlayer, audioElements, getCustomDisplayName]);

  // Update displayed track name only when revealed
  useEffect(() => {
    if (isRevealed) {
      setDisplayedTrackName(currentTrackName);
    } else {
      setDisplayedTrackName('');
    }
  }, [isRevealed, currentTrackName]);

  // Update current time and position marker
  useEffect(() => {
    const audioElement = audioElements.get(selectedPlayer);
    if (!audioElement || !showPosition) return;

    const updateTime = () => {
      setCurrentTime(audioElement.currentTime);
      if (positionMarkerRef.current && audioElement.duration > 0) {
        const position = (audioElement.currentTime / audioElement.duration) * 100;
        positionMarkerRef.current.style.left = `${position}%`;
      }
    };

    // Use event-driven updates instead of polling
    audioElement.addEventListener('timeupdate', updateTime);
    return () => audioElement.removeEventListener('timeupdate', updateTime);
  }, [selectedPlayer, audioElements, showPosition]);

  const handleReveal = () => {
    setIsRevealed(true);  // Show the track name
  };

  const handleTogglePosition = () => {
    setShowPosition(!showPosition);
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audioElement = audioElements.get(selectedPlayer);
    if (!audioElement || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const percentage = clickPosition / rect.width;
    const newTime = percentage * audioElement.duration;
    
    // Use seekToTime to synchronize all players instead of just the selected one
    seekToTime(newTime);
    setCurrentTime(newTime);
    
    // Update last start positions if playing
    if (isPlaying) {
      const newStartPositions = new Map<number, number>();
      audioElements.forEach((audio, playerNumber) => {
        newStartPositions.set(playerNumber, audio.currentTime);
      });
      setLastStartPositions(newStartPositions);
    }
  };

  // Add keyboard shortcut for reveal
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'r' && !isRevealed && currentTrackName) {
        handleReveal();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isRevealed, currentTrackName]);

  // Handle Tab key to cycle through tabs (desktop only)
  useEffect(() => {
    if (isMobile) return; // Only enable on desktop

    const handleTabKey = (event: KeyboardEvent) => {
      // Skip if user is editing text
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement ||
          (event.target instanceof HTMLElement && event.target.contentEditable === 'true')) {
        return;
      }

      // Only handle Tab key when in blind test mode
      if (event.key === 'Tab' && blindTestMode) {
        event.preventDefault();
        
        // Cycle to next tab (wrap around)
        const nextIndex = (tabIndex + 1) % 3;
        setTabIndex(nextIndex);
        
        const modes = ['basic', 'pick-fave', 'guess-track'] as const;
        setBlindTestMode(modes[nextIndex]);
        pauseAll();
      }
    };

    window.addEventListener('keydown', handleTabKey);
    return () => window.removeEventListener('keydown', handleTabKey);
  }, [isMobile, tabIndex, blindTestMode, setBlindTestMode, pauseAll]);

  // Sync tabIndex with blindTestMode when mode changes externally
  useEffect(() => {
    const modeToIndex: Record<string, number> = {
      'basic': 0,
      'pick-fave': 1,
      'guess-track': 2
    };
    if (blindTestMode && modeToIndex[blindTestMode] !== undefined) {
      setTabIndex(modeToIndex[blindTestMode]);
    }
  }, [blindTestMode]);

  // Helper to check if any track is over 10 minutes
  const hasLongTracks = (audioElements: Map<number, HTMLAudioElement>): boolean => {
    for (const audio of audioElements.values()) {
      if (audio.duration && audio.duration > 600) { // 600 seconds = 10 minutes
        return true;
      }
    }
    return false;
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const useLeadingZero = hasLongTracks(audioElements);
    const minutesStr = useLeadingZero 
      ? minutes.toString().padStart(2, '0')
      : minutes.toString();
    return `${minutesStr}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box w="full" h="full" position="absolute" top={0} left={0} right={0} bottom={0} pt="10px" bg="gray.900">
      <Tabs 
        variant="enclosed" 
        colorScheme="accent" 
        index={tabIndex}
        h="full"
        display="flex"
        flexDirection="column"
        onChange={(index) => {
          setTabIndex(index);
          const modes = ['basic', 'pick-fave', 'guess-track'] as const;
          setBlindTestMode(modes[index]);
          pauseAll();
        }}
        sx={{
          '.chakra-tabs__tab': {
            _selected: {
              color: 'accent.400',
              borderColor: 'accent.500',
              borderBottomColor: 'gray.800',
              bg: 'gray.800',
              borderTopRadius: '6px'
            },
            _active: {
              bg: 'transparent'
            },
            _focus: {
              boxShadow: 'none',
              outline: 'none'
            },
            color: 'gray.400',
            borderColor: 'gray.700',
            bg: 'gray.900',
            fontSize: { base: 'sm', md: 'md' },
            px: { base: 2, md: 4 },
            py: 2,
            height: 'auto',
            borderTopRadius: '6px'
          },
          '.chakra-tabs__tab-panel': {
            borderColor: 'accent.500',
            borderWidth: '1px',
            borderRadius: '6px',
            bg: 'gray.800',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: { base: 2, md: 4 }
          },
          '.chakra-tabs__tablist': {
            borderBottom: 'none',
            bg: 'gray.900'
          }
        }}
      >
        <TabList justifyContent="center">
          <Tooltip 
            label={
              <HStack spacing={2} color="gray.800">
                <Text>Change Blind Test mode</Text>
                <TooltipKeyBox>⇥</TooltipKeyBox>
              </HStack>
            }
            placement="top"
            hasArrow
            isDisabled={isMobile}
          >
            <Tab>Basic</Tab>
          </Tooltip>
          <Tooltip 
            label={
              <HStack spacing={2} color="gray.800">
                <Text>Change Blind Test mode</Text>
                <TooltipKeyBox>⇥</TooltipKeyBox>
              </HStack>
            }
            placement="top"
            hasArrow
            isDisabled={isMobile}
          >
            <Tab>Pick Fave</Tab>
          </Tooltip>
          <Tooltip 
            label={
              <HStack spacing={2} color="gray.800">
                <Text>Change Blind Test mode</Text>
                <TooltipKeyBox>⇥</TooltipKeyBox>
              </HStack>
            }
            placement="top"
            hasArrow
            isDisabled={isMobile}
          >
            <Tab>Guess Track</Tab>
          </Tooltip>
        </TabList>

        <TabPanels flex={1}>
          <TabPanel 
            h="full" 
            display="flex" 
            alignItems="center"
            maxW="500px"
            mx="auto"
            w="full"
          >
            <VStack spacing={6} align="stretch" w="full" p={2}>
              <Center px={4}>
                <Text color="gray.300" fontSize="sm" textAlign="center">
                  Press <Box as="span"
                    width="20px"
                    height="20px"
                    minWidth="20px"
                    minHeight="20px"
                    maxWidth="20px"
                    maxHeight="20px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="accent.500"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    color="accent.400"
                    fontSize="sm"
                    fontWeight="bold"
                    lineHeight="1"
                    overflow="hidden"
                    mx={1}
                  >
                    ?
                  </Box> to switch, then <Icon as={FaEye} mx={1} verticalAlign="middle" boxSize="22px" color="#F9D69C" /> to reveal.
                </Text>
              </Center>

              <VStack spacing={8} position="relative" h={{ base: "250px", md: "280px" }}>
                <Text 
                  fontSize="xl" 
                  color="accent.400" 
                  textAlign="center" 
                  minH="40px"
                  mt={4}
                >
                  {isRevealed && displayedTrackName ? displayedTrackName.replace(/:/g, '/') : '?'}
                </Text>

                <Button
                  ref={buttonRef}
                  onClick={handleReveal}
                  colorScheme="accent"
                  variant="outline"
                  size="lg"
                  width="200px"
                  mx="auto"
                  isDisabled={!selectedPlayer}
                  leftIcon={<FaEye />}
                  _hover={{
                    transform: 'scale(1.05)',
                    borderColor: 'accent.400',
                    bg: 'transparent'
                  }}
                  _active={{
                    transform: 'scale(0.95)',
                    borderColor: 'accent.500',
                    bg: 'transparent'
                  }}
                >
                  Reveal
                </Button>

                <Button
                  onClick={handleTogglePosition}
                  colorScheme="accent"
                  variant="outline"
                  size="md"
                  width="120px"
                  mx="auto"
                  isDisabled={!selectedPlayer}
                  border="none"
                  _hover={{
                    color: 'accent.400',
                    bg: 'transparent'
                  }}
                  _active={{
                    color: 'accent.500',
                    bg: 'transparent'
                  }}
                >
                  {showPosition ? 'Hide Position' : 'Show Position'}
                </Button>

                <Tooltip 
                  label={blindTestAllowRepeats ? "Random switches may select the same track again" : "Random switches will always select a different track"} 
                  placement="top"
                  hasArrow
                >
                  <FormControl display="flex" alignItems="center" justifyContent="center" mt={4}>
                    <FormLabel htmlFor="allow-repeats-basic" mb={0} color="gray.300" fontSize="sm" cursor="help">
                      Allow repeats
                    </FormLabel>
                    <Switch
                      id="allow-repeats-basic"
                      isChecked={blindTestAllowRepeats}
                      onChange={(e) => setBlindTestAllowRepeats(e.target.checked)}
                      colorScheme="accent"
                    />
                  </FormControl>
                </Tooltip>

                <Box 
                  ref={timelineRef}
                  onClick={handleTimelineClick}
                  bg="gray.900"
                  borderRadius="md"
                  p={0.5}
                  position="absolute"
                  bottom="10px"
                  left="50%"
                  transform="translateX(-50%)"
                  minH="20px"
                  h="20px"
                  w="80%"
                  cursor="pointer"
                  display={showPosition ? "block" : "none"}
                  zIndex={5}
                >
                  <Box
                    ref={positionMarkerRef}
                    position="absolute"
                    top={0}
                    bottom={0}
                    width="2px"
                    bg="accent.500"
                    zIndex={10}
                    style={{ left: '0%' }}
                  />
                </Box>
              </VStack>
            </VStack>
          </TabPanel>

          <TabPanel 
            h="full" 
            display="flex" 
            alignItems="center"
            maxW="500px"
            mx="auto"
            w="full"
          >
            <PickYourFaveView playerNumberToFileId={playerNumberToFileId} />
          </TabPanel>

          <TabPanel 
            h="full" 
            display="flex" 
            alignItems="center"
            maxW="500px"
            mx="auto"
            w="full"
          >
            <GuessTheTrackView playerNumberToFileId={playerNumberToFileId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default BlindTestView; 